import { Texture } from 'pixi.js';

let cached: Texture | null = null;

/**
 * One soft white glow, drawn once and shared. Lights and the chase use it
 * as a tinted sprite and only animate its alpha and scale, which stays
 * cheap on low-end phones.
 */
export function glowTexture(): Texture {
  if (cached) return cached;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  cached = Texture.from(canvas);
  return cached;
}

let shadow: Texture | null = null;

/**
 * A soft dark ellipse's falloff, drawn once: stretched flat under a
 * landmark's base, it sits the landmark on the grass.
 */
export function shadowTexture(): Texture {
  if (shadow) return shadow;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(24,16,4,0.78)');
    g.addColorStop(0.55, 'rgba(24,16,4,0.5)');
    g.addColorStop(1, 'rgba(24,16,4,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  shadow = Texture.from(canvas);
  return shadow;
}

let bulb: Texture | null = null;

/**
 * A glass bulb lit from the top left, drawn in greys so a tint gives its
 * colour: warm brown when off, pink or gold when lit.
 */
export function bulbTexture(): Texture {
  if (bulb) return bulb;
  const size = 48;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size * 0.35, size * 0.35, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(0.5, '#c4c4c4');
    g.addColorStop(1, '#4e4e4e');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
  }
  bulb = Texture.from(canvas);
  return bulb;
}

/** True when the player has asked the system for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** How often a stepped light moves when motion is reduced. */
export const REDUCED_STEP_MS = 300;
